// ---------------------------------------------------------------------------
// Flight-tracking utilities — shared between WarRoomModal and useFlightTracking
// ---------------------------------------------------------------------------

export const IATA_TO_ICAO = {
  UA: "UAL", LH: "DLH", AC: "ACA", NH: "ANA", OZ: "AAR",
  SK: "SAS", TK: "THY", OS: "AUA", LO: "LOT", CA: "CCA",
  JP: "ADR", ZH: "CSZ", SN: "BEL", OU: "CTN", MS: "MSR",
  SQ: "SIA", TG: "THA", AY: "FIN", LX: "SWR", ET: "ETH",
  SA: "SAA", EG: "EGF", BR: "EVA", EY: "EYU", NZ: "ANZ",
  AZ: "ITY", AI: "AIC", EK: "UAE", QF: "QFA", CX: "CPA",
  MH: "MAS", TN: "THT", GF: "GFA", WY: "OMA", KE: "KAL",
  CI: "CAL", MU: "CES", HU: "CHH",
  CM: "CMP", TP: "TAP", A3: "AEE",
  AA: "AAL", DL: "DAL", AS: "ASA", B6: "JBU", WN: "SWA",
};

export const PROVIDERS = [
  { key: "opensky", label: "OpenSky" },
  // adsb.lol / adsb.fi / airplanes.live all block browser CORS — routed through
  // a Supabase Edge Function proxy that calls them server-side.
  { key: "proxy",   label: "ADS-B"  },
];

// Normalised aircraft object: { callsign, lat, lon, altM, speedMs, heading, onGround, vertRateFpm }

export function normaliseAdsbAc(ac) {
  // Shared schema for adsb.lol, adsb.fi, airplanes.live
  // alt_baro is in feet; gs is in knots
  return {
    callsign: (ac.flight || "").trim().toUpperCase(),
    lat: ac.lat,
    lon: ac.lon,
    altM: typeof ac.alt_baro === "number" ? ac.alt_baro / 3.28084 : null,
    speedMs: ac.gs != null ? ac.gs * 0.514444 : null,
    heading: ac.track ?? 0,
    onGround: !!ac.ground,
    vertRateFpm: ac.baro_rate ?? null,
  };
}

export async function fetchOpenSky(callsigns, signal) {
  const res = await fetch(
    "https://opensky-network.org/api/states/all?lamin=0&lomin=-180&lamax=72&lomax=-55",
    { signal }
  );
  if (res.status === 429) { const e = new Error("429"); e.rateLimited = true; throw e; }
  if (!res.ok) throw new Error("HTTP " + res.status);
  const data = await res.json();
  const csSet = new Set(callsigns);
  return (data.states || [])
    .filter(sv => sv[1] && csSet.has(sv[1].trim().toUpperCase()) && sv[5] !== null && sv[6] !== null)
    .map(sv => ({
      callsign: sv[1].trim().toUpperCase(),
      lat: sv[6], lon: sv[5],
      altM: sv[7],
      speedMs: sv[9],
      heading: sv[10] ?? 0,
      onGround: !!sv[8],
      vertRateFpm: sv[11] != null ? sv[11] * 196.85 : null, // m/s → ft/min
    }));
}

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_KEY;

export async function fetchFromProxy(callsigns, signal, icao24s = []) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/adsb-proxy`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "apikey": SUPABASE_KEY,
      "Authorization": `Bearer ${SUPABASE_KEY}`,
    },
    body: JSON.stringify({ callsigns, icao24s }),
    signal,
  });
  if (!res.ok) throw new Error("Proxy HTTP " + res.status);
  const data = await res.json();
  if (data.error) throw new Error("Proxy error: " + data.error);
  return (data.ac || [])
    .filter(ac => ac.lat != null && ac.lon != null)
    .map(normaliseAdsbAc);
}

export async function lookupCallsign(flightIata, signal) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/flight-callsign`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "apikey": SUPABASE_KEY,
      "Authorization": `Bearer ${SUPABASE_KEY}`,
    },
    body: JSON.stringify({ flight_iata: flightIata }),
    signal,
  });
  if (!res.ok) throw new Error("Callsign lookup HTTP " + res.status);
  const data = await res.json();
  if (data.error) throw new Error("Callsign lookup error: " + data.error);
  return data; // { icao24, registration, callsign }
}

export async function fetchFromProvider(key, callsigns, signal) {
  switch (key) {
    case "opensky": return fetchOpenSky(callsigns, signal);
    case "proxy":   return fetchFromProxy(callsigns, signal);
    default: throw new Error("Unknown provider: " + key);
  }
}

export function flightToIcaoCallsign(flt) {
  if (!flt) return null;
  const m = flt.match(/^([A-Z]{2})(\d+)(.*)$/i);
  if (!m) return null;
  const iata = m[1].toUpperCase();
  const num  = m[2];
  const suffix = m[3];
  const icao = IATA_TO_ICAO[iata];
  if (!icao) return null;
  return icao + num + suffix;
}

// Some airlines broadcast zero-padded callsigns (e.g. EVA012 instead of EVA12).
// Returns all shorter-to-longer padded variants up to 3 digits (e.g. EVA2 → ["EVA02","EVA002"]).
export function paddedCallsigns(cs) {
  if (!cs) return [];
  const m = cs.match(/^([A-Z]+)(\d+)(.*)$/);
  if (!m) return [];
  const prefix = m[1], num = m[2], suffix = m[3];
  const variants = [];
  for (let len = num.length + 1; len <= 3; len++) {
    variants.push(prefix + num.padStart(len, "0") + suffix);
  }
  return variants;
}

// UA flights with 4-digit numbers (3000-5999) are often operated by SkyWest (SKW)
// under United Express and broadcast "SKW####" rather than "UAL####" in ADS-B.
export function regionalCallsigns(iataFlt) {
  if (!iataFlt) return [];
  const m = iataFlt.match(/^UA(\d{4})$/i);
  if (!m) return [];
  const num = m[1];
  return parseInt(num, 10) >= 3000 ? [`SKW${num}`] : [];
}

// Derive a display-ready flight status from a normalised aircraft vector.
// Returns "FINAL_DESCENT", "LANDED", "TAKING_OFF", or null (no badge needed).
export function deriveFlightStatus(sv) {
  if (!sv) return null;
  const altFt = sv.altM != null ? Math.round(sv.altM * 3.28084 / 100) * 100 : null;
  const speedKts = sv.speedMs != null ? sv.speedMs * 1.94384 : null;
  const isOnGround = sv.onGround || altFt === null || altFt <= 50;
  if (isOnGround) {
    // Any ground movement > 3 kts = departed the gate (pushback/taxi counts)
    return (speedKts != null && speedKts > 3) ? "TAKING_OFF" : "LANDED";
  }
  const vr = sv.vertRateFpm;
  if (vr != null && vr < -200 && altFt != null && altFt < 10000) return "FINAL_DESCENT";
  return null; // cruising/climbing/descending — no badge needed
}
